import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Badge, Alert, Form, Table } from 'react-bootstrap';
import api from "../../services/api";
import Loading from '../Shared/Loading';
import TableFull from '../Shared/CommonTable/TableFull';
import { toast } from 'react-toastify';
import { dateAsNumbersWithoutSeconds } from '../../utils/dateFormatter';
import styles from './PushNotificationsManagement.module.scss';

// Keyed by the enum's int, which is how the API sends it: PushNotificationStatuses
const NOTIFICATION_STATUS_VARIANTS = {
  1: 'warning',   // Pending
  2: 'success',   // Completed
  3: 'danger',    // CompletedWithFailures
  4: 'secondary', // NoTargets
  5: 'secondary', // SendingDisabled
};

// PushDeliveryStatuses
const DELIVERY_STATUS_VARIANTS = {
  1: 'warning',   // Pending
  2: 'warning',   // RetryPending
  3: 'success',   // Accepted
  4: 'danger',    // Failed
  5: 'danger',    // TokenRejected
  6: 'secondary', // TargetGone
};

// "Pixel 8 · 2.4.1 · Guard", or whichever of the parts is known
const describePhone = (deviceModel, appVersion, appName) => [deviceModel, appVersion, appName].filter(Boolean).join(' · ');

// The pinned phone by model and version; its app is the App column (a pinned send copies the phone's app type)
const describeAimedPhone = item => describePhone(item.aimedDeviceModel, item.aimedAppVersion, null);

export default function PushNotificationsManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // A phone is named by its uid only. The link comes from the phones page, which lists phones that exist now
  const targetUidFilter = (searchParams.get('targetUid') || '').toLowerCase() || null;

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [items, setItems] = useState([]);
  const [recentDays, setRecentDays] = useState(null);

  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    loadList();
  }, []);

  const loadList = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/admin/push-notifications');
      setItems(response.data.items);
      setRecentDays(response.data.recentDays);
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
      toast.error(t('admin.pushNotifications.loadError'));
      console.error('Error loading push notifications, status:', error?.response?.status);
    } finally {
      setLoading(false);
    }
  };

  // TableFull keeps its page in ?page= and never resets it, so a filter applied on a later page would show nothing.
  // Built from the current params: react-router 6.2 has no functional setSearchParams, a callback there wipes the query
  const dropSearchParams = (...names) => {
    const next = new URLSearchParams(searchParams);
    names.forEach(name => next.delete(name));
    setSearchParams(next, { replace: true });
  };

  const filteredItems = useMemo(() => {
    const email = emailFilter.trim().toLowerCase();
    return items.filter(x =>
      // A notification aimed at the device counts even when nothing was sent to it (NoTargets has no delivery rows)
      (targetUidFilter === null || x.aimedPushTargetUid === targetUidFilter || x.deliveries.some(d => d.pushTargetUid === targetUidFilter)) &&
      (!email || (x.userLoginEmail || '').toLowerCase().includes(email)) &&
      (!statusFilter || String(x.status) === statusFilter));
  }, [items, targetUidFilter, emailFilter, statusFilter]);

  // What the filter banner calls the phone, taken from any row about it
  const targetFilterLabel = useMemo(() => {
    if (targetUidFilter === null) return '';
    for (const x of items) {
      if (x.aimedPushTargetUid === targetUidFilter) return describePhone(x.aimedDeviceModel, x.aimedAppVersion, x.appType ? t(`admin.pushTargets.appTypeNames.${x.appType}`) : null);
      const delivery = x.deliveries.find(d => d.pushTargetUid === targetUidFilter);
      if (delivery) return describePhone(delivery.deviceModel, null, delivery.appType ? t(`admin.pushTargets.appTypeNames.${delivery.appType}`) : null);
    }
    return '';
  }, [items, targetUidFilter, t]);

  const columns = useMemo(() => [
    {
      header: t('admin.pushNotifications.created'),
      accessorKey: 'dateCreatedUtc',
      cell: info => dateAsNumbersWithoutSeconds(info.getValue()),
    },
    {
      header: t('admin.pushNotifications.login'),
      accessorKey: 'userLoginEmail',
      cell: info => info.getValue() || <Badge bg="danger">{t('admin.pushNotifications.loginMissing')}</Badge>,
    },
    {
      header: t('admin.pushNotifications.text'),
      accessorKey: 'body',
      // Up to 1000 characters: unwrapped it runs under the next cells and widens the page
      allowWrap: true,
      cell: ({ row }) => (
        <>
          {row.original.title && <strong>{row.original.title}: </strong>}
          {row.original.body}
        </>
      ),
    },
    {
      header: t('admin.pushNotifications.sentTo'),
      id: 'sentTo',
      accessorFn: x => x.isAimedAtOnePhone ? describeAimedPhone(x) : '',
      allowWrap: true,
      cell: ({ row }) => {
        const item = row.original;
        if (!item.isAimedAtOnePhone) {
          return t('admin.pushNotifications.sentToAllDevices');
        }
        // No uid means the phone does not exist any more; its model, if shown, is what the delivery row kept
        return (
          <>
            {describeAimedPhone(item) || t('admin.pushNotifications.sentToOneDevice')}{' '}
            {!item.aimedPushTargetUid && <Badge bg="secondary">{t('admin.pushNotifications.phoneRemoved')}</Badge>}
          </>
        );
      },
    },
    {
      // The notification's own addressing, not the app it reached; a pinned send to a pre-1568 phone is addressed to every app
      header: t('admin.pushNotifications.appType'),
      accessorKey: 'appType',
      cell: info => info.getValue() == null ? t('admin.pushNotifications.allApps') : t(`admin.pushTargets.appTypeNames.${info.getValue()}`),
    },
    {
      header: t('admin.pushNotifications.statusHeader'),
      accessorKey: 'status',
      cell: info => (
        <Badge bg={NOTIFICATION_STATUS_VARIANTS[info.getValue()] || 'secondary'}>
          {t(`admin.pushNotifications.status.${info.getValue()}`)}
        </Badge>
      ),
    },
    {
      header: t('admin.pushNotifications.deliveries'),
      accessorKey: 'acceptedCount',
      cell: ({ row }) => t('admin.pushNotifications.acceptedOf', { accepted: row.original.acceptedCount, total: row.original.deliveryCount }),
    },
    {
      header: t('admin.pushNotifications.completed'),
      accessorKey: 'completedUtc',
      cell: info => info.getValue() ? dateAsNumbersWithoutSeconds(info.getValue()) : '-',
    },
  ], [t]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.filters}>
          <Form.Control
            type="text"
            value={emailFilter}
            onChange={e => { setEmailFilter(e.target.value); dropSearchParams('page'); }}
            placeholder={t('admin.pushNotifications.emailFilterPlaceholder')}
          />
          <Form.Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); dropSearchParams('page'); }}>
            <option value="">{t('admin.pushNotifications.allStatuses')}</option>
            {Object.keys(NOTIFICATION_STATUS_VARIANTS).map(s => <option key={s} value={s}>{t(`admin.pushNotifications.status.${s}`)}</option>)}
          </Form.Select>
        </div>
        <div className={styles.headerActions}>
          <Button variant="outline-primary" onClick={loadList}>
            {t('admin.pushNotifications.refresh')}
          </Button>
        </div>
      </div>

      {recentDays !== null && (
        <p className="text-muted">{t('admin.pushNotifications.recentOnly', { days: recentDays })}</p>
      )}

      {targetUidFilter !== null && (
        <Alert variant="info" dismissible onClose={() => dropSearchParams('targetUid', 'page')}>
          {targetFilterLabel
            ? t('admin.pushNotifications.filteredByTarget', { phone: targetFilterLabel })
            : t('admin.pushNotifications.filteredByUnnamedTarget')}
        </Alert>
      )}

      {filteredItems.length === 0 ? (
        <div className={styles.emptyState}>
          {/* A failed load must not present itself as an empty list */}
          <Alert variant={loadFailed ? 'danger' : 'info'}>
            {loadFailed ? t('admin.pushNotifications.loadError') : t('admin.pushNotifications.noNotifications')}
          </Alert>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <TableFull
            data={filteredItems}
            columns={columns}
            styles={styles}
            allowColumnHeaderSort={true}
            mainTableClassName={styles.table}
            onRowClick={setSelected}
          />
        </div>
      )}

      <Modal show={!!selected} onHide={() => setSelected(null)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.pushNotifications.deliveriesTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              <p>
                <strong>{selected.userLoginEmail || t('admin.pushNotifications.loginMissing')}</strong>{' '}
                {dateAsNumbersWithoutSeconds(selected.dateCreatedUtc)}
              </p>
              <p>
                {selected.title && <strong>{selected.title}: </strong>}
                {selected.body}
              </p>
              {selected.deliveries.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  {/* Why nothing was sent differs by status: not processed yet, sending off, or no phone to send to */}
                  {t(`admin.pushNotifications.noDeliveriesByStatus.${selected.status}`, t('admin.pushNotifications.noDeliveries'))}
                </Alert>
              ) : (
                <Table size="sm" responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('admin.pushNotifications.delivery.appType')}</th>
                      <th>{t('admin.pushNotifications.delivery.platform')}</th>
                      <th>{t('admin.pushNotifications.delivery.deviceModel')}</th>
                      <th>{t('admin.pushNotifications.statusHeader')}</th>
                      <th>{t('admin.pushNotifications.delivery.attempts')}</th>
                      <th>{t('admin.pushNotifications.delivery.lastAttempt')}</th>
                      <th>{t('admin.pushNotifications.delivery.accepted')}</th>
                      <th>{t('admin.pushNotifications.delivery.lastError')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.deliveries.map(d => (
                      <tr key={d.uid}>
                        <td>{t(`admin.pushTargets.appTypeNames.${d.appType}`)}</td>
                        <td>{t(`admin.pushTargets.platformNames.${d.platform}`)}</td>
                        <td>
                          {/* The model is the snapshot taken at send time; no uid means the phone was removed since */}
                          {d.deviceModel || '-'}{' '}
                          {!d.pushTargetUid && <Badge bg="secondary">{t('admin.pushNotifications.phoneRemoved')}</Badge>}
                        </td>
                        <td>
                          <Badge bg={DELIVERY_STATUS_VARIANTS[d.status] || 'secondary'}>
                            {t(`admin.pushNotifications.deliveryStatus.${d.status}`)}
                          </Badge>
                        </td>
                        <td>{d.attemptCount}</td>
                        <td>{d.lastAttemptUtc ? dateAsNumbersWithoutSeconds(d.lastAttemptUtc) : '-'}</td>
                        <td>{d.acceptedUtc ? dateAsNumbersWithoutSeconds(d.acceptedUtc) : '-'}</td>
                        <td>{d.lastError ? t(`admin.pushNotifications.deliveryError.${d.lastError}`) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              <p className="text-muted mt-3 mb-0">{t('admin.pushNotifications.acceptedIsNotDelivered')}</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)}>
            {t('common.closeButton')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
